import { todayISO } from "./dates";
import Log from "../types/Log";
import { Timestamp } from "../types/Timestamp";
import Tracking from "../types/Tracking";
import { WorkType } from "../types/Work";

// An episode by its place: its number, in its season. Once a show is one
// work (ADR 0030) its episodes, and the logs that cover them, carry their
// season, so S1 E3 and S2 E3 are two episodes of one work. A legacy season's
// episodes, a book's chapters and every log on them have none: their numbers
// run through the work, as they always have. A Segment and a Continue card's
// Episode are each one.
export type EpisodeKey = { season?: number | null; number: number };

const seasonPart = (season: number | null | undefined): string =>
  `${season ?? ""}:`;

// An episode as a Map's or a Set's key: "2:3" for S2 E3, ":3" for an episode
// with no season.
export const episodeKey = ({ season, number }: EpisodeKey): string =>
  `${seasonPart(season)}${number}`;

// A request's season, spread into its body: there only when it's known, so a
// request about an episode or a log without one is what it always was, and a
// PATCH never sets a season it wasn't given.
export const knownSeason = (
  season: number | null | undefined,
): { season?: number } => (season != null ? { season } : {});

// A log's season, spread into a body that writes the log back: only with its
// range. A log in a season is a range of its episodes (ADR 0030's Decision
// 3), and the API answers a season sent without one with a 400. A season
// started or dropped with no episodes logged, a shape only the server makes,
// goes back without it: a PATCH leaves the log's season as it is, and a POST
// makes it the whole show.
export const logSeason = ({
  season,
  segmentEnd,
}: Pick<Log, "season" | "segmentEnd">): { season?: number } =>
  segmentEnd != null ? knownSeason(season) : {};

// A log's episode or chapter range: "E3", "E3–5" for TV ("S2 E3–5" in a
// season); "ch. 3–5" otherwise.
export const segmentRange = (
  {
    season,
    segmentStart: start,
    segmentEnd: end,
  }: Pick<Log, "season" | "segmentStart" | "segmentEnd">,
  type: WorkType,
): string | null => {
  if (!start) return null;
  const range = end && end !== start ? `${start}–${end}` : `${start}`;
  if (type !== "tv") return `ch. ${range}`;
  return `${season != null ? `S${season} ` : ""}E${range}`;
};

// What a history row says of a season started or dropped with no episodes
// logged (ADR 0030's Decision 3, a shape only the server makes): "Started
// S2" while it's open, "Dropped in S3" once dropped. Null for any other log,
// which reads as its verb and its `segmentRange`.
export const seasonMark = ({
  season,
  segmentEnd,
  finishedAt,
  dropped,
}: Pick<Log, "season" | "segmentEnd" | "finishedAt" | "dropped">):
  | string
  | null => {
  if (season == null || segmentEnd != null) return null;
  if (!finishedAt) return `Started S${season}`;
  // A finish needs a range (the API's `ck_logs_season_shape`).
  return dropped ? `Dropped in S${season}` : null;
};

// Every episode an entry's logs cover, by `episodeKey`, with when it was
// watched (null when the log has no finish date).
export type Watched = Map<string, Timestamp | null>;

export const watchedEpisodes = (entry: Tracking | null): Watched => {
  const watched: Watched = new Map();
  for (const log of entry?.logs ?? []) {
    if (log.segmentEnd == null) continue;
    for (let n = log.segmentStart ?? 1; n <= log.segmentEnd; n++) {
      const key = episodeKey({ season: log.season, number: n });
      if (!watched.has(key)) watched.set(key, log.finishedAt);
    }
  }
  return watched;
};

// The furthest episode watched in a season (or, given none, among episodes
// with no season), 0 before any: the next one comes after it.
export const furthestWatched = (
  watched: Watched,
  season: number | null | undefined,
): number => {
  const prefix = seasonPart(season);
  let furthest = 0;
  for (const key of watched.keys()) {
    if (key.startsWith(prefix))
      furthest = Math.max(furthest, Number(key.slice(prefix.length)));
  }
  return furthest;
};

// How many episodes of a season (or, given none, with no season) are
// watched.
export const countWatched = (
  watched: Watched,
  season: number | null | undefined,
): number => {
  const prefix = seasonPart(season);
  return [...watched.keys()].filter((key) => key.startsWith(prefix)).length;
};

// An episode as a label names it: "S2 E3" in a season, "E3" without one.
export const episodeLabel = ({ season, number }: EpisodeKey): string =>
  `${season != null ? `S${season} ` : ""}E${number}`;

// A run of episodes as a label names it: "S3 E1–2" in one season, "S1 E1–S3
// E2" across seasons, "E1–2" without them.
export const episodeRange = (from: EpisodeKey, to: EpisodeKey): string =>
  (from.season ?? null) === (to.season ?? null)
    ? `${episodeLabel(to).replace(/E\d+$/, "")}E${from.number}–${to.number}`
    : `${episodeLabel(from)}–${episodeLabel(to)}`;

// Whether an episode is out: undated ones count as aired (only announced
// episodes carry a future date).
export const hasAired = (airDate: string | null): boolean =>
  !airDate || airDate <= todayISO();
